/**
 * Une jauge dessinée — ⟦A-6⟧ « jauges dessinées, pile gribouillée » : piste au marqueur,
 * remplissage en hachures obliques, jamais un aplat propre. Tout en CSS (PLAN §8).
 *
 * `verrouillee` est la seule exception à la règle A-5 « absente, pas grisée » : l'avenant
 * **A-1 point 8** demande explicitement la jauge Argent **grisée avec son tooltip
 * pédagogique** avant le Grand Saut.
 *
 * ⟦A-20⟧ **Une jauge a le droit de dépasser son maximum**, et ça doit se voir. Le `Math.min(1, …)`
 * de la fraction écrasait la surcharge d'énergie : à 125 ⚡ pour un plafond de 130, la barre était
 * pleine — exactement comme à 100. Le moteur calculait une mécanique que l'écran rendait
 * invisible.
 *
 * Mathieu, sur ce que la barre doit faire : *« il faut qu'on imagine la jauge qui puisse aller
 * au-delà de 100 pour l'énergie, peut-être ça change de couleur sur le haut de la 100 et ça va
 * plus loin »*. `repere` est cette prop : la piste se gradue sur le plafond **étendu**, un trait
 * au marqueur marque l'ancien 100, et ce qui le dépasse change de pigment.
 *
 * ⚠️ Composant partagé par les **sept** jauges. `repere` est optionnel et n'est passé que par
 * l'énergie : sans lui, pas un nœud de DOM ni une classe de plus, et la fraction est celle
 * d'avant. Les six autres jauges sont rigoureusement inchangées.
 */

import { DIT, GLYPHE, SEUIL_DEFAUT, useTendance24h } from "./tendance24h";

export interface JaugeProps {
  icone: string;
  nom: string;
  /** Valeur courante, dans l'unité de la jauge. */
  valeur: number;
  min?: number;
  max?: number;
  /**
   * ⟦A-20⟧ Repère du plafond **ordinaire**, quand `max` est un plafond *étendu* (surcharge).
   * La piste se gradue alors sur `max`, un trait au marqueur marque `repere`, et le segment
   * au-delà passe à `--gauge-surcharge`.
   *
   * Omis (le cas normal, ⟦A-10⟧ état neutre), la jauge est celle d'avant, au pixel près.
   */
  repere?: number;
  /** Variable CSS de teinte (`--gauge-energie`…). */
  teinte: string;
  /** Texte affiché à droite ; par défaut la valeur arrondie. */
  affichage?: string;
  verrouillee?: boolean;
  /** Tooltip — sert au message pédagogique de la jauge Argent (A-1). */
  titre?: string;
  /**
   * ⟦SPEC-UI §3.1⟧ **La flèche de tendance sur 24 h simulées.** Une clé STABLE identifiant la
   * jauge dans la run (« energie », « sm »…) : c'est elle qui indexe la mémoire d'interface.
   *
   * Omise (le cas par défaut, ⟦A-10⟧), rien n'est mesuré, rien n'est écrit, aucun nœud de DOM
   * n'est ajouté — le panneau de dev et le récapitulatif restent au pixel près ce qu'ils sont.
   */
  suivi?: string;
  /**
   * Le seuil de §3.1 (±2) vaut pour une jauge **0-100**. Sur le Momentum (0,8-1,5) il ne
   * voudrait rien dire : une jauge d'une autre échelle passe le sien, ou ne demande pas de
   * flèche du tout — ce que fait la maquette de §3.1, qui n'en met que sur les quatre.
   */
  seuilTendance?: number;
  /**
   * ⟦A-129 · C11⟧ Pose `data-decouverte-ancre` sur la racine de la jauge — c'est là que
   * `InfoBulleDecouverte` vient ancrer sa fiche non bloquante. Omis (le cas par défaut), aucun
   * attribut de plus : la jauge reste au pixel près celle d'avant.
   */
  ancre?: string;
  /**
   * ⟦C21 · A-146 §2⟧ Le halo pulsé de Matt (`.halo-pulse`) : cette jauge est celle dont il parle
   * maintenant (`game/parole.ts`, `haloDeReplique`). Défaut `false` — état neutre par défaut,
   * aucune jauge n'en portait avant ce chantier.
   */
  halo?: boolean;
  /**
   * ⟦C62 · lot tableau · A-179⟧ **Le NOM de la jauge, écrit.** Aucune jauge n'avait de mot : le nom ne vivait que
   * dans l'infobulle `title`, qui n'existe pas au toucher (audit du 21/10/2026, friction n° 1). À `true`, `nom`
   * s'écrit à côté de l'icône, et la ligne passe sur deux rangées (icône, nom, valeur, flèche, puis la piste
   * sur toute la largeur) : une colonne de 280 px ne tient pas « Santé mentale » ET une piste lisible sur une
   * seule.
   *
   * Omis (le cas par défaut, ⟦A-10⟧), pas un nœud de DOM ni une classe de plus : la jauge est celle d'avant,
   * au pixel près. Un nom de plus à l'écran ne remplace pas l'`aria-label`, il le double pour les yeux.
   */
  nomVisible?: boolean;
}

export function Jauge({
  icone,
  nom,
  valeur,
  min = 0,
  max = 100,
  repere,
  teinte,
  affichage,
  verrouillee = false,
  titre,
  suivi,
  seuilTendance = SEUIL_DEFAUT,
  ancre,
  halo = false,
  nomVisible = false,
}: JaugeProps) {
  // ⚠️ Appel INCONDITIONNEL : c'est `suivi` qui est optionnel, jamais le hook (règle React).
  const tendance = useTendance24h(suivi, valeur, seuilTendance);
  const etendue = Math.max(Number.EPSILON, max - min);
  const part = Math.max(0, Math.min(1, (valeur - min) / etendue));
  const partRepere =
    repere === undefined ? null : Math.max(0, Math.min(1, (repere - min) / etendue));
  // Le débord n'existe QUE si la valeur passe le repère : un plafond ouvert sur une jauge encore
  // basse n'affiche aucun débord, seulement son trait. « Au-dessus de 100 » et « autorisé à
  // dépasser » sont deux états distincts, et les confondre mentirait dans l'autre sens.
  const debord = partRepere !== null && partRepere < 1 && part > partRepere;
  const teinteCss = { ["--teinte" as string]: `var(${teinte})` };
  return (
    <div
      className={`jauge${verrouillee ? " verrouillee" : ""}${debord ? " en-surcharge" : ""}${halo ? " halo-pulse" : ""}${nomVisible ? " jauge-nommee" : ""}`}
      title={titre}
      data-decouverte-ancre={ancre}
      aria-label={`${nom} : ${affichage ?? Math.round(valeur)}${
        debord ? ` — au-dessus de ${repere}, plafond ${max}` : ""
      }${verrouillee ? " (verrouillé)" : ""}${tendance ? ` — ${DIT[tendance]}` : ""}`}
    >
      <span className="jauge-icone" aria-hidden="true">
        {icone}
      </span>
      {/* ⟦C62 · lot tableau⟧ Le nom écrit : décoratif pour un lecteur d'écran (l'`aria-label` de la jauge et
          celui du `meter` le disent déjà), lisible au toucher et sans survol pour tout le monde. */}
      {nomVisible ? (
        <span className="jauge-nom" aria-hidden="true">
          {nom}
        </span>
      ) : null}
      <div
        className="jauge-piste"
        role="meter"
        aria-valuenow={Math.round(valeur)}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label={nom}
      >
        <div className="jauge-remplissage" style={{ width: `${part * 100}%`, ...teinteCss }} />
        {/* Le débord porte sa propre couleur (`--gauge-surcharge`), pas `--teinte` : c'est tout
            l'objet de la demande — au-dessus du repère, ça CHANGE de couleur. */}
        {debord ? (
          <div
            className="jauge-debord"
            style={{ left: `${partRepere * 100}%`, width: `${(part - partRepere) * 100}%` }}
          />
        ) : null}
        {partRepere !== null && partRepere < 1 ? (
          <span
            className="jauge-repere"
            style={{ left: `${partRepere * 100}%` }}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <span className="jauge-valeur">{affichage ?? Math.round(valeur)}</span>
      {/* ⟦SPEC-UI §3.1⟧ ▲ / ▼ / ▬. Rien du tout tant qu'il n'y a pas de veille à comparer :
          un ▬ affiché le premier jour dirait « ça n'a pas bougé » là où la vérité est « je ne
          sais pas encore ». Le sens est déjà dans l'`aria-label` de la jauge, donc ce glyphe
          est décoratif pour un lecteur d'écran. */}
      {tendance ? (
        <span className={`jauge-tendance tendance-${tendance}`} aria-hidden="true">
          {GLYPHE[tendance]}
        </span>
      ) : null}
    </div>
  );
}
